import LensIcon from '@mui/icons-material/Lens';
import { Box } from '@mui/material';
import {
  type GridColDef,
  type GridRowsProp,
} from '@mui/x-data-grid';
import { useMemo } from 'react';

import CameraDelete from './CameraDelete.js';
import CameraForm from './CameraForm.js';
import CameraVisionSnippetsButton from './CameraVisionSnippetsButton.js';
import { useAllCameras } from '../../api/index.js';
import { useCheckPermissions } from '../../auth/index.js';
import useDialogControls from '../../hooks/use-dialog-controls.js';
import { type Camera } from '../../models/index.js';
import Table from '../Table/Table.js';
import tableActions from '../Table/tableActions.js';

const VISIBLE_FIELDS = ['patientName', 'name', 'motionDetection', 'status', 'vision', 'actions'];

const CamerasTable = () => {
  const { data = [], refetch, isError, isLoading } = useAllCameras();

  const {
    open: formOpen,
    selectedData: formData,
    onOpen: onFormOpen,
    reset: resetForm,
  } = useDialogControls<Camera>(data);
  const {
    open: deleteOpen,
    selectedData: deleteData,
    onOpen: onDelete,
    reset: resetDelete,
  } = useDialogControls<Camera>(data);
  const { canManageCamera, canDeleteCamera, canManageVisionDebug } = useCheckPermissions();

  const columns: GridColDef[] = useMemo(() => {
    const cols: GridColDef[] = [
      { field: 'patientName', headerName: 'Patient Name', flex: 1 },
      { field: 'name', headerName: 'Name', flex: 1 },
      {
        field: 'motionDetection',
        headerName: 'Motion Detection',
        type: 'boolean',
        width: 200,
        align: 'center',
        headerAlign: 'center',
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 200,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <LensIcon color={params.value ? 'success' : 'error'} />
          </Box>
        ),
      },
    ];
    if (canManageVisionDebug) {
      cols.push({
        field: 'vision',
        headerName: 'Vision',
        width: 120,
        sortable: false,
        renderCell: (params) => (
          <CameraVisionSnippetsButton cameraId={params.row.id as string} cameraName={params.row.name as string} />
        ),
      });
    }
    if (canManageCamera || canDeleteCamera) {
      cols.push({
        field: 'actions',
        headerName: 'Actions',
        width: 100,
        sortable: false,
        type: 'actions',
        getActions: (params) => tableActions(onDelete(params.id), onFormOpen(params.id))({
          canEdit: canManageCamera,
          canDelete: canDeleteCamera,
        }),
      });
    }
    // Otherwise filter will be applied on fields such as the hidden column id
    return cols.filter((column) => VISIBLE_FIELDS.includes(column.field));
  }, [canManageCamera, canDeleteCamera, canManageVisionDebug, onFormOpen, onDelete]);

  const rows: GridRowsProp = useMemo(() => {
    return data.map(({ id, name, patient, lastReachable, motionDetection, visionSettings }) => {
      return {
        id,
        patientName: `${patient.firstName} ${patient.lastName}`,
        name,
        motionDetection,
        visionSettings,
        status: lastReachable && new Date(lastReachable).getTime() > new Date().getTime() - 10 * 60 * 1000,
      };
    });
  }, [data]);

  if (isError) {
    return <h1>Error loading cameras</h1>;
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ my: 1 }}>
        <h2 style={{ margin: 0 }}>Cameras</h2>
      </Box>
      <Table
        columns={columns}
        rows={rows}
        NoRowsOverlayText={'No cameras available.'}
        isLoading={isLoading}
        pageSize={10}
        pageSizeOptions={[10, 25, 50, 100]}
        toolbar
      />
      <CameraForm
        open={formOpen}
        onClose={resetForm}
        camera={formData}
        patientId={formData?.patientId}
        refetch={refetch}
        patientName={`${formData?.patient?.firstName} ${formData?.patient?.lastName}`}
      />
      <CameraDelete
        open={deleteOpen}
        onClose={resetDelete}
        camera={deleteData}
        refetch={refetch}
      />
    </Box>
  );
};

export default CamerasTable;
